/**
 * IndexedDB Service for Local Flashcard Persistence and Sync Queue.
 * Optimized for performance and offline-first reliability.
 */

const DB_NAME = 'libritui_flashcards_local_db';
const DB_VERSION = 2;

export interface LocalFeedback {
  card_id: string;
  history: string;
  difficulty_score: number;
  next_review_date?: string;
  updated_at: string;
  reviewDueAtTimestamp?: number;
}

export interface SyncTask {
  id?: number;
  type: 'srs';
  card_id: string;
  quality: string;
  timestamp: number;
}

class FlashcardLocalDb {
  private db: IDBDatabase | null = null;

  private async getDb(): Promise<IDBDatabase> {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('feedback')) {
          db.createObjectStore('feedback', { keyPath: 'card_id' });
        }
        if (!db.objectStoreNames.contains('sync_queue')) {
          db.createObjectStore('sync_queue', { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains('store_decks')) {
          db.createObjectStore('store_decks', { keyPath: 'id' });
        }
      };

      request.onsuccess = (event: any) => {
        this.db = event.target.result;
        this.db.onversionchange = () => {
          console.warn("[FlashcardLocalDb] Database version changed. Closing connection.");
          this.db?.close();
          this.db = null;
        };
        this.db.onclose = () => {
          console.warn("[FlashcardLocalDb] Database connection closed unexpectedly.");
          this.db = null;
        };
        resolve(this.db!);
      };

      request.onerror = (event: any) => {
        console.error("[FlashcardLocalDb] Error opening DB:", event.target.error);
        reject(event.target.error);
      };
    });
  }

  async saveFeedback(feedback: LocalFeedback): Promise<void> {
    const execute = async (dbInstance: IDBDatabase): Promise<void> => {
      return new Promise((resolve, reject) => {
        const transaction = dbInstance.transaction(['feedback'], 'readwrite');
        const store = transaction.objectStore('feedback');
        const request = store.put(feedback);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    };

    let db = await this.getDb();
    try {
      return await execute(db);
    } catch (err: any) {
      if (err && (err.name === 'InvalidStateError' || err.message?.includes('closing') || err.message?.includes('closed'))) {
        console.warn("[FlashcardLocalDb] DB closed/closing on saveFeedback. Re-opening...");
        this.db = null;
        db = await this.getDb();
        return await execute(db);
      }
      throw err;
    }
  }

  async getFeedback(cardId: string): Promise<LocalFeedback | null> {
    const execute = async (dbInstance: IDBDatabase): Promise<LocalFeedback | null> => {
      return new Promise((resolve, reject) => {
        const transaction = dbInstance.transaction(['feedback'], 'readonly');
        const store = transaction.objectStore('feedback');
        const request = store.get(String(cardId));
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });
    };

    let db = await this.getDb();
    try {
      return await execute(db);
    } catch (err: any) {
      if (err && (err.name === 'InvalidStateError' || err.message?.includes('closing') || err.message?.includes('closed'))) {
        console.warn("[FlashcardLocalDb] DB closed/closing on getFeedback. Re-opening...");
        this.db = null;
        db = await this.getDb();
        return await execute(db);
      }
      throw err;
    }
  }

  async getAllFeedback(): Promise<Record<string, LocalFeedback>> {
    const execute = async (dbInstance: IDBDatabase): Promise<Record<string, LocalFeedback>> => {
      return new Promise((resolve, reject) => {
        const transaction = dbInstance.transaction(['feedback'], 'readonly');
        const store = transaction.objectStore('feedback');
        const request = store.getAll();
        request.onsuccess = () => {
          const map: Record<string, LocalFeedback> = {};
          request.result.forEach((f: LocalFeedback) => {
            map[f.card_id] = f;
          });
          resolve(map);
        };
        request.onerror = () => reject(request.error);
      });
    };

    let db = await this.getDb();
    try {
      return await execute(db);
    } catch (err: any) {
      if (err && (err.name === 'InvalidStateError' || err.message?.includes('closing') || err.message?.includes('closed'))) {
        console.warn("[FlashcardLocalDb] DB closed/closing on getAllFeedback. Re-opening...");
        this.db = null;
        db = await this.getDb();
        return await execute(db);
      }
      throw err;
    }
  }

  async enqueueSync(task: SyncTask): Promise<void> {
    const execute = async (dbInstance: IDBDatabase): Promise<void> => {
      return new Promise((resolve, reject) => {
        const transaction = dbInstance.transaction(['sync_queue'], 'readwrite');
        const store = transaction.objectStore('sync_queue');
        const request = store.add(task);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    };

    let db = await this.getDb();
    try {
      return await execute(db);
    } catch (err: any) {
      if (err && (err.name === 'InvalidStateError' || err.message?.includes('closing') || err.message?.includes('closed'))) {
        console.warn("[FlashcardLocalDb] DB closed/closing on enqueueSync. Re-opening...");
        this.db = null;
        db = await this.getDb();
        return await execute(db);
      }
      throw err;
    }
  }

  async getNextSyncTask(): Promise<SyncTask | null> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['sync_queue'], 'readonly');
      const store = transaction.objectStore('sync_queue');
      const request = store.openCursor();
      request.onsuccess = () => resolve(request.result ? request.result.value : null);
      request.onerror = () => reject(request.error);
    });
  }

  async dequeueSync(id: number): Promise<void> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['sync_queue'], 'readwrite');
      const store = transaction.objectStore('sync_queue');
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getSyncQueueCount(): Promise<number> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['sync_queue'], 'readonly');
      const store = transaction.objectStore('sync_queue');
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async cleanupOldFeedback(days: number = 30): Promise<void> {
    const db = await this.getDb();
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const cutoffStr = cutoff.toISOString();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['feedback'], 'readwrite');
      const store = transaction.objectStore('feedback');
      const request = store.openCursor();
      
      request.onsuccess = (event: any) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.updated_at < cutoffStr) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async clearSyncQueue(): Promise<void> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['sync_queue'], 'readwrite');
      const store = transaction.objectStore('sync_queue');
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async saveStoreDeck(id: string, data: any[]): Promise<void> {
    const execute = async (dbInstance: IDBDatabase): Promise<void> => {
      return new Promise((resolve, reject) => {
        const transaction = dbInstance.transaction(['store_decks'], 'readwrite');
        const store = transaction.objectStore('store_decks');
        const request = store.put({ id, data });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    };

    let db = await this.getDb();
    try {
      return await execute(db);
    } catch (err: any) {
      if (err && (err.name === 'InvalidStateError' || err.message?.includes('closing') || err.message?.includes('closed'))) {
        console.warn("[FlashcardLocalDb] DB closed/closing on saveStoreDeck. Re-opening...");
        this.db = null;
        db = await this.getDb();
        return await execute(db);
      }
      throw err;
    }
  }

  async getStoreDeck(id: string): Promise<any[] | null> {
    const execute = async (dbInstance: IDBDatabase): Promise<any[] | null> => {
      return new Promise((resolve, reject) => {
        const transaction = dbInstance.transaction(['store_decks'], 'readonly');
        const store = transaction.objectStore('store_decks');
        const request = store.get(id);
        request.onsuccess = () => resolve(request.result ? request.result.data : null);
        request.onerror = () => reject(request.error);
      });
    };

    let db = await this.getDb();
    try {
      return await execute(db);
    } catch (err: any) {
      if (err && (err.name === 'InvalidStateError' || err.message?.includes('closing') || err.message?.includes('closed'))) {
        console.warn("[FlashcardLocalDb] DB closed/closing on getStoreDeck. Re-opening...");
        this.db = null;
        db = await this.getDb();
        return await execute(db);
      }
      throw err;
    }
  }

  async deleteStoreDeck(id: string): Promise<void> {
    const execute = async (dbInstance: IDBDatabase): Promise<void> => {
      return new Promise((resolve, reject) => {
        const transaction = dbInstance.transaction(['store_decks'], 'readwrite');
        const store = transaction.objectStore('store_decks');
        const request = store.delete(id);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    };

    let db = await this.getDb();
    try {
      return await execute(db);
    } catch (err: any) {
      if (err && (err.name === 'InvalidStateError' || err.message?.includes('closing') || err.message?.includes('closed'))) {
        console.warn("[FlashcardLocalDb] DB closed/closing on deleteStoreDeck. Re-opening...");
        this.db = null;
        db = await this.getDb();
        return await execute(db);
      }
      throw err;
    }
  }

  async getAllStoreDeckKeys(): Promise<string[]> {
    const execute = async (dbInstance: IDBDatabase): Promise<string[]> => {
      return new Promise((resolve, reject) => {
        const transaction = dbInstance.transaction(['store_decks'], 'readonly');
        const store = transaction.objectStore('store_decks');
        const request = store.getAllKeys();
        request.onsuccess = () => resolve(request.result as string[]);
        request.onerror = () => reject(request.error);
      });
    };

    let db = await this.getDb();
    try {
      return await execute(db);
    } catch (err: any) {
      if (err && (err.name === 'InvalidStateError' || err.message?.includes('closing') || err.message?.includes('closed'))) {
        console.warn("[FlashcardLocalDb] DB closed/closing on getAllStoreDeckKeys. Re-opening...");
        this.db = null;
        db = await this.getDb();
        return await execute(db);
      }
      throw err;
    }
  }
}

export const localFlashcardDb = new FlashcardLocalDb();
